import * as mobx from 'mobx'
import { RootStore, StatsStore } from 'bzx-common/src/stores'
import { ChainId } from 'bzx-common/src/config/appConfig'
import regexUtils from 'app-lib/regexUtils'
import { DialogVM } from 'bzx-common/src/ui-framework'

interface IUserProfileVMProps {
  selectedChain: ChainId | null
  avatar: number
  avatars: number[]
  alias: string
  inputAlias: string
  warning: string
  avatarPagination: number
  txPagination: number
}

export default class UserProfileVM implements IUserProfileVMProps {
  rootStore: RootStore
  statsStore: StatsStore
  selectedChain: ChainId | null = null
  avatar = 0
  avatars: number[] = []
  alias = ''
  inputAlias = ''
  avatarPagination = 0
  txPagination = 0
  warning = ''
  avatarDialog = new DialogVM({ id: 'avatar-dialog' })
  desktopVisible = 15
  mobileVisible = 10
  paginationSize = 5
  avatarsCount = 100

  private stopAutoSyncData?: mobx.IReactionDisposer

  get txInfo() {
    return this.statsStore.latestUserTransactions.value.filter(
      (tx) => !this.selectedChain || tx.chainId === this.selectedChain
    )
  }

  get defaultQuantityVisible() {
    const { isMobileMedia } = this.rootStore.uiStore.media
    return isMobileMedia ? this.mobileVisible : this.desktopVisible
  }

  get currentAvatars() {
    const startIndex = this.defaultQuantityVisible * this.avatarPagination
    const endIndex =
      this.defaultQuantityVisible * this.avatarPagination + this.defaultQuantityVisible

    return this.avatars.slice(startIndex, endIndex)
  }

  get currentTransactions() {
    const startIndex = this.defaultQuantityVisible * this.txPagination
    const endIndex = this.defaultQuantityVisible * this.txPagination + this.defaultQuantityVisible

    return this.txInfo.slice(startIndex, endIndex)
  }

  get quantityAvatarPagination() {
    return Math.ceil(this.avatars.length / this.defaultQuantityVisible)
  }

  get quantityTxPagination() {
    return Math.ceil(this.txInfo.length / this.defaultQuantityVisible)
  }

  get isLastAvatarPagination() {
    return (
      this.avatarPagination + 1 === this.quantityAvatarPagination ||
      this.avatars.length <= this.defaultQuantityVisible
    )
  }

  get isLastTxPagination() {
    return (
      this.txPagination + 1 === this.quantityTxPagination ||
      this.txInfo.length <= this.defaultQuantityVisible
    )
  }

  get avatarPaginations() {
    let startIndex = this.avatarPagination - Math.floor(this.paginationSize / 2)

    if (startIndex < 1) {
      startIndex = 0
    } else if (startIndex + this.paginationSize > this.quantityTxPagination) {
      startIndex = this.quantityAvatarPagination - this.paginationSize
    }

    const endIndex = startIndex + this.paginationSize

    return [...Array(this.quantityAvatarPagination)].map((x, i) => i).slice(startIndex, endIndex)
  }

  get txPaginations() {
    let startIndex = this.txPagination - Math.floor(this.paginationSize / 2)

    if (startIndex < 1) {
      startIndex = 0
    } else if (startIndex + this.paginationSize > this.quantityTxPagination) {
      startIndex = this.quantityTxPagination - this.paginationSize
    }

    const endIndex = startIndex + this.paginationSize

    return [...Array(this.quantityTxPagination)].map((x, i) => i).slice(startIndex, endIndex)
  }

  get errorMessage() {
    return this.statsStore.latestUserTransactions.error
      ? 'The latest transactions could not be fetched.'
      : !this.currentTransactions.length
      ? 'No activity found.'
      : ''
  }

  get needSave() {
    const { shortWalletAddress } = this.rootStore.web3Connection
    return this.inputAlias !== this.alias || this.inputAlias === shortWalletAddress
  }

  get canSave() {
    return !this.warning && this.alias !== this.inputAlias
  }

  getAvatars() {
    const { settings } = this.rootStore
    // get unused avatars only
    const avatars = []

    for (let ava = 1; ava <= this.avatarsCount; ava++) {
      !Object.values(settings.users).find((user) => user.avatar === ava) && avatars.push(ava)
    }
    this.assign({ avatars })
  }

  toggleStored(storedEvents: boolean) {
    this.statsStore.assign({ storedEvents })
  }

  ediAlias(alias: string) {
    const { settings } = this.rootStore
    this.assign({ inputAlias: alias })

    const warning =
      Object.values(settings.users).find(
        (user) => user.alias.toLowerCase() === alias.toLowerCase()
      ) && alias !== this.alias
        ? 'Alias exists'
        : alias.length > 2 && alias.length < 18
        ? regexUtils.matchAlias(alias)
        : 'From 3 to 18 symbols required'

    this.assign({ warning })
  }

  saveAlias() {
    const { settings, web3Connection } = this.rootStore
    this.set('alias', this.inputAlias)
    settings.setAlias(web3Connection.connectedAddress, this.inputAlias)
  }

  restoreDefaults() {
    const { settings, web3Connection } = this.rootStore
    const { shortWalletAddress } = web3Connection
    this.assign({ avatar: 0, alias: shortWalletAddress, inputAlias: shortWalletAddress })
    settings.setAvatar(web3Connection.connectedAddress, 0)
    this.saveAlias()
  }

  saveAvatar() {
    const { settings, web3Connection } = this.rootStore
    const { connectedAddress } = web3Connection
    settings.setAvatar(connectedAddress, this.avatar)
  }

  updateAvatar(avatar: number) {
    this.assign({ avatar })
    this.closeDialog()
  }

  closeDialog() {
    this.avatarDialog.hide()
    this.saveAvatar()
  }

  nextPagination = () => {
    !this.isLastAvatarPagination && this.set('avatarPagination', this.avatarPagination + 1)
  }

  prevPagination = () => {
    this.avatarPagination !== 0 && this.set('avatarPagination', this.avatarPagination - 1)
  }

  nextTxPagination = () => {
    !this.isLastTxPagination && this.set('txPagination', this.txPagination + 1)
  }

  prevTxPagination = () => {
    this.txPagination !== 0 && this.set('txPagination', this.txPagination - 1)
  }
  /**
   * Helper to set values through mobx actions.
   */
  set(prop: keyof IUserProfileVMProps, value: any) {
    ;(this[prop] as any) = value
  }

  assign(props: Partial<IUserProfileVMProps>) {
    Object.assign(this, props)
  }

  syncData() {
    const { web3Connection, settings } = this.rootStore
    const storedUser = settings.users[web3Connection.walletAddress]
    const alias = storedUser ? storedUser.alias : web3Connection.shortWalletAddress
    const avatar = storedUser && storedUser.avatar
    this.getAvatars()
    this.assign({
      avatar,
      alias,
      inputAlias: alias,
      warning: '',
    })
  }

  init() {
    this.syncData()
    this.stopAutoSyncData = mobx.reaction(
      () => this.rootStore.web3Connection.walletAddress,
      () => {
        const { isConnected } = this.rootStore.web3Connection
        if (isConnected) {
          this.syncData()
          this.statsStore.latestUserTransactions.fetch()
        }
      },
      { name: 'autosyncTransactionsVM' }
    )
  }

  destroyVM() {
    this.stopAutoSyncData && this.stopAutoSyncData()
  }

  constructor({ rootStore }: { rootStore: RootStore }) {
    this.rootStore = rootStore
    this.statsStore = rootStore.statsStore
    this.init()
    mobx.makeAutoObservable(this, undefined, { deep: false, autoBind: true })
    !this.statsStore.latestUserTransactions.loaded && this.statsStore.latestUserTransactions.fetch()
  }
}
